interface HPRingProps {
  currentHp: number;
  maxHp: number;
  tempHp?: number;
  size?: 'sm' | 'md' | 'lg';
  onDamage?: () => void;
  onHeal?: () => void;
}

export function HPRing({
  currentHp,
  maxHp,
  tempHp = 0,
  size = 'md',
  onDamage,
  onHeal,
}: HPRingProps) {
  const percentage = maxHp > 0 ? Math.max(0, Math.min(100, (currentHp / maxHp) * 100)) : 0;

  // Ring dimensions based on size
  const dimensions = {
    sm: { container: 120, radius: 50, strokeWidth: 8, fontSize: '2rem', labelSize: '0.5rem' },
    md: { container: 160, radius: 70, strokeWidth: 12, fontSize: '2.5rem', labelSize: '0.65rem' },
    lg: { container: 200, radius: 85, strokeWidth: 14, fontSize: '3rem', labelSize: '0.75rem' },
  };

  const dim = dimensions[size];
  const circumference = 2 * Math.PI * dim.radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color based on HP percentage using site theme
  const getColor = () => {
    if (percentage > 50) return { stroke: '#4caf50', shadow: 'rgba(76, 175, 80, 0.4)' }; // success
    if (percentage > 25) return { stroke: '#ff9800', shadow: 'rgba(255, 152, 0, 0.4)' }; // warning
    return { stroke: '#f44336', shadow: 'rgba(244, 67, 54, 0.4)' }; // error
  };

  const color = getColor();
  const isDying = currentHp === 0;

  return (
    <div className="flex flex-col items-center">
      {/* Ring Container */}
      <div
        className="relative"
        style={{ width: dim.container, height: dim.container }}
      >
        <svg
          className="transform -rotate-90"
          style={{ width: dim.container, height: dim.container }}
          viewBox={`0 0 ${dim.container} ${dim.container}`}
        >
          {/* Background Ring */}
          <circle
            cx={dim.container / 2}
            cy={dim.container / 2}
            r={dim.radius}
            fill="none"
            stroke="#252220"
            strokeWidth={dim.strokeWidth}
          />

          {/* HP Ring */}
          <circle
            cx={dim.container / 2}
            cy={dim.container / 2}
            r={dim.radius}
            fill="none"
            stroke={color.stroke}
            strokeWidth={dim.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease',
              filter: `drop-shadow(0 0 8px ${color.shadow})`,
            }}
            className={isDying ? 'animate-pulse' : ''}
          />
        </svg>

        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="font-mono font-bold leading-none text-text-primary"
            style={{ fontSize: dim.fontSize }}
          >
            {currentHp}
          </span>
          <span className="font-mono text-text-muted" style={{ fontSize: `calc(${dim.fontSize} * 0.4)` }}>
            / {maxHp}
          </span>
          <span
            className="font-display text-accent-gold uppercase tracking-widest mt-1"
            style={{ fontSize: dim.labelSize }}
          >
            Hit Points
          </span>
        </div>
      </div>

      {/* Controls */}
      {(onDamage || onHeal) && (
        <div className="flex gap-2 mt-3">
          {onDamage && (
            <button
              onClick={onDamage}
              className="px-4 py-2 bg-error/20 hover:bg-error/40 text-text-primary rounded-md
                font-display text-sm tracking-wider transition-colors"
            >
              - DAMAGE
            </button>
          )}
          {onHeal && (
            <button
              onClick={onHeal}
              className="px-4 py-2 bg-success/20 hover:bg-success/40 text-text-primary rounded-md
                font-display text-sm tracking-wider transition-colors"
            >
              + HEAL
            </button>
          )}
        </div>
      )}

      {/* Temp HP */}
      {tempHp > 0 && (
        <div className="mt-2 px-3 py-1 bg-class-wizard/20 border border-class-wizard/50 rounded-md flex items-center gap-2">
          <span className="text-text-secondary text-sm">Temp HP</span>
          <span className="font-mono font-semibold text-class-wizard">{tempHp}</span>
        </div>
      )}
    </div>
  );
}
